import { supabase } from "@/integrations/supabase/client";
import { type AuthenticatorAssuranceLevels, isAuthSessionMissingError } from "@supabase/supabase-js";
import { type TotpFactor, toTotpFactors } from "./mfaFactors";

export type ConfirmedMFAStatus = {
  status: "ok";
  factors: TotpFactor[];
  hasEnrolledFactors: boolean;
  hasCompletedMFA: boolean;
  aal: AuthenticatorAssuranceLevels;
};

export type MFAStatus = ConfirmedMFAStatus | { status: "unavailable" };

export const MFA_LOOKUP_RETRY_DELAYS_MS = [500, 1500] as const;

type Attempt = { result: MFAStatus; isWorthRetrying: boolean };

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const failedAttempt = (message: string, error: unknown): Attempt => {
  console.error(message, error);
  return { result: { status: "unavailable" }, isWorthRetrying: !isAuthSessionMissingError(error) };
};

const lookupOnce = async (): Promise<Attempt> => {
  try {
    const assurance = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (assurance.error) return failedAttempt("Error checking MFA assurance level:", assurance.error);

    const listed = await supabase.auth.mfa.listFactors();
    const verifiedTotp = listed.data?.totp;
    if (listed.error || !verifiedTotp) return failedAttempt("Error listing MFA devices:", listed.error);

    const factors = toTotpFactors(verifiedTotp);
    const aal = assurance.data.currentLevel ?? "aal1";

    return {
      result: { status: "ok", factors, hasEnrolledFactors: factors.length > 0, hasCompletedMFA: aal === "aal2", aal },
      isWorthRetrying: false,
    };
  } catch (error) {
    return failedAttempt("Error checking MFA status:", error);
  }
};

export const lookupMFAStatus = async () => {
  let attempt = await lookupOnce();
  for (const delay of MFA_LOOKUP_RETRY_DELAYS_MS) {
    if (!attempt.isWorthRetrying) break;
    await wait(delay);
    attempt = await lookupOnce();
  }

  return attempt.result;
};
